/**
 * Matriz de roles y permisos.
 *
 * Esta matriz se aplica EN EL SERVIDOR. La interfaz la usa unicamente para
 * ocultar controles que el usuario no podria ejecutar; ocultar un boton no es
 * control de acceso. Todo endpoint sensible revalida el permiso.
 *
 * Los permisos se otorgan POR SUCURSAL (`user_store_roles`). Un mismo usuario
 * puede ser dueño en una tienda y empleado en otra.
 */
/**
 * `cashier` —"Caja (gerente)"— SE RETIRO del sistema el 14 de agosto de 2026.
 *
 * No se retiro del menu como `manager`: se borro de `role_permissions` y de las
 * dos restricciones `check` (migracion 20260814100000), porque no quedaba nadie
 * con ese rol. En el mostrador solo hay Empleado.
 *
 * Que se pueda borrar de verdad depende de que NADIE lo tenga: una membresia
 * apuntando a un rol sin permisos no da error, da una persona que entra con su
 * contraseña correcta y no puede hacer nada, sin nada que lo explique. La
 * migracion convierte antes de borrar, y lo comprueba.
 */
export const ROLES = ['owner', 'manager', 'employee'];
/**
 * Los roles que la dueña puede ASIGNAR hoy.
 *
 * `manager` no esta aqui y sigue existiendo: es un rol vivo en la base y en las
 * membresias que ya se otorgaron, pero dejo de ofrecerse en el alta de personal.
 * Un rol retirado del menu no es un rol invalido — quitarlo de `ROLES` dejaria
 * sin etiqueta ni permisos a quien ya lo tiene, que es la peor forma de
 * "limpiar" una matriz de acceso. (Es justo lo que SI se pudo hacer con
 * `cashier`, porque ahi no quedaba nadie.)
 */
export const ASSIGNABLE_ROLES = ['owner', 'employee'];
export const ROLE_LABELS = {
    owner: 'Dueño',
    manager: 'Gerente',
    employee: 'Empleado',
};
export const ROLE_DESCRIPTIONS = {
    owner: 'Control total: usuarios, sucursales, precios, finanzas y auditoria.',
    manager: 'Opera la tienda: inventario, compras, gastos, precios y reportes.',
    // Se lee en la tarjeta del alta de personal, asi que dice lo que NO puede
    // tanto como lo que puede: es donde la dueña decide a quien le da esto.
    employee: 'Solo cobra. Puede abrir la caja, pero no hace el corte, no fia y no toca el inventario.',
};
export const PERMISSIONS = [
    // Punto de venta
    'sale:create',
    'sale:cancel',
    'sale:view_all', // ver ventas de otros usuarios, no solo las propias
    // Catalogo
    'product:view',
    'product:create',
    'product:update', // incluye cambiar precio de venta
    'product:archive', // descontinuar: sale del catalogo, su historia se queda
    'product:delete', // borrar de verdad, y solo si nunca se movio
    // Inventario
    'inventory:adjust', // ajuste manual de existencias
    'inventory:shrinkage', // merma: caducado, roto, robado
    'inventory:view_cost', // ver costos y margenes
    // Abasto
    'purchase:manage',
    'supplier:manage',
    // Proponer una entrada de mercancia, y firmarla. Son dos actos distintos y de
    // dos personas distintas: quien recibe al proveedor captura lo que llego, y
    // quien responde por el dinero decide si entra al inventario. Aceptar mueve
    // existencia, escribe kardex y recalcula el costo promedio ponderado.
    'intake:submit',
    'intake:approve',
    // Dinero
    'expense:create',
    'expense:delete',
    'cash:open',
    'cash:close',
    'cash:movement', // entradas y salidas de efectivo del cajon
    'cash:view_all', // ver cortes de otros usuarios
    // Credito (fiado)
    'customer:view', // el cajero necesita elegir a quien se le fia
    'customer:manage', // alta, edicion y limite de credito
    'credit:collect', // registrar abonos: el dinero se recibe en el mostrador
    'credit:adjust', // corregir o condonar deuda: toca el dinero sin que entre un peso
    // Analisis
    'report:operations',
    'report:finance', // utilidad, margenes, costo de inventario
    'intelligence:view',
    // Administracion
    'user:manage',
    'store:manage',
    'audit:view',
];
/**
 * El empleado: el piso de la tienda, y SOLO COBRA.
 *
 * Cobra, busca en el catalogo lo que esta cobrando, y puede abrir el cajon para
 * no tener que esperar a nadie a las siete de la mañana. Ahi se acaba: no cierra
 * la caja —contar el dinero y firmar la diferencia es de la dueña—, no fia, no
 * captura entradas de mercancia, no ve el panel ni el surtido, y no puede
 * modificar absolutamente nada.
 *
 * Son tres permisos, y esa cortedad es el punto: cada uno que se agregue aqui
 * hay que poder explicarlo enfrente del mostrador.
 *
 * `product:view` no es "ver el inventario": es lo que necesita el buscador de la
 * Caja para encontrar lo que se esta cobrando. La pantalla de Inventario se le
 * oculta por otro permiso — ver la tabla `NAV` de `components/Shell.tsx`.
 */
const EMPLOYEE_PERMISSIONS = [
    'sale:create',
    'product:view',
    'cash:open',
];
/**
 * OJO AL REESCRIBIR ESTA CADENA.
 *
 * Hasta el 14 de agosto de 2026 el gerente heredaba de `cashier` y `cashier` de
 * `employee`. Al retirarse el rol Caja y quitarsele `intake:submit` al empleado,
 * dejar el `spread` como estaba le habria quitado al GERENTE siete permisos sin
 * que nada fallara ni nadie se enterara — hasta que un gerente no pudiera fiar.
 *
 * Por eso lo que venia por herencia esta escrito a mano abajo. La cuenta tiene
 * que dar 25 para el gerente y 31 para el dueño, que es lo que hay en
 * `role_permissions`, y lo comprueba `permissions.test.ts`.
 *
 * Eran 27 y 33 hasta el 19 de agosto de 2026, cuando se retiraron los TRASPASOS
 * entre sucursales y con ellos `transfer:create` y `transfer:receive`. Ahi la
 * diferencia importa: esos dos no se PERDIERON por limpiar la cadena —que es de
 * lo que avisa este comentario—, se fueron con su funcion. Cada sucursal vende
 * productos distintos y el traspaso nunca tuvo pantalla; una sucursal nueva se
 * surte dando de alta sus propios productos y registrando su entrada de
 * mercancia. (`adopt_store_product` se retiro el 22 de agosto de 2026 con el
 * catalogo por sucursal: ya no hay de donde traer nada.)
 */
const MANAGER_PERMISSIONS = [
    ...EMPLOYEE_PERMISSIONS,
    // --- Lo que era del rol "Caja" (retirado). No se puede perder. ------------
    'cash:close',
    'cash:movement',
    'expense:create',
    // Fiar y recibir abonos son actos de mostrador: quien encarga la tienda los
    // hace de todos modos, y no dejarlo registrarlos solo garantiza que queden en
    // un cuaderno.
    'customer:view',
    'credit:collect',
    // Proponer una entrada de mercancia. Era del empleado y subio aqui: el
    // empleado solo cobra.
    'intake:submit',
    // --- Propios del gerente --------------------------------------------------
    'sale:cancel',
    'sale:view_all',
    'product:create',
    'product:update',
    'product:archive',
    'inventory:adjust',
    'inventory:shrinkage',
    'inventory:view_cost',
    'purchase:manage',
    'supplier:manage',
    'expense:delete',
    'cash:view_all',
    'report:operations',
    'report:finance',
    'intelligence:view',
    'customer:manage',
];
const OWNER_PERMISSIONS = [
    ...MANAGER_PERMISSIONS,
    'user:manage',
    'store:manage',
    'audit:view',
    // Perdonar una deuda es sacar dinero del negocio sin que se mueva un peso.
    // Solo la dueña.
    'credit:adjust',
    // Descontinuar es del gerente y se deshace; borrar no se deshace. La
    // diferencia no es de confianza, es de reversibilidad.
    'product:delete',
    // Aceptar lo que capturo el empleado: mueve existencia y recalcula el costo.
    // Es la firma que convierte una propuesta en mercancia.
    'intake:approve',
];
const MATRIX = {
    employee: new Set(EMPLOYEE_PERMISSIONS),
    manager: new Set(MANAGER_PERMISSIONS),
    owner: new Set(OWNER_PERMISSIONS),
};
/** Unico predicado de autorizacion del sistema. */
export function can(role, permission) {
    return MATRIX[role].has(permission);
}
/** True si el rol tiene TODOS los permisos indicados. */
export function canAll(role, permissions) {
    return permissions.every((p) => can(role, p));
}
/** True si el rol tiene AL MENOS UNO de los permisos indicados. */
export function canAny(role, permissions) {
    return permissions.some((p) => can(role, p));
}
/** Lista completa de permisos de un rol. Se envia al cliente al iniciar sesion. */
export function permissionsFor(role) {
    return [...MATRIX[role]];
}
export function isRole(value) {
    return typeof value === 'string' && ROLES.includes(value);
}
//# sourceMappingURL=permissions.js.map